import React, {useMemo, useState, useEffect} from 'react';
import PropTypes from 'prop-types';

//MUI
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import { getCurrentYear } from '../../../../../utils/getCurrentYear';
import { getMinYearCO2Interventions } from '../../../../../utils/getMinYearCO2Interventions';

//BIKENTA
import AbsorptionPlanSeccion from '../AbsorptionPlanSection';
import BiomasSection from '../BiomasSection';
import Co2_InterventionMenu from './Co2_InterventionMenu';
import InventoriesMenu from './InventoriesMenu';
import MonitoringMassDescription from './MonitoringMassDescription';
import LazyLoading from '../../../../LazyLoading';

//UTILS
import {BASELINE_CO2, MONITORING, SPECIES_INCLUDED_BIOMASCO2} from '../../../../../config';
import {inventory} from '../../../../proptypes/inventory';
import {expostProject} from '../../../../proptypes/expostProject';
import {useTranslation} from 'react-i18next';
import {expostProjectToCO2Interventions} from '../../../../../utils/expostProjectToCO2Interventions';
import {composeExpostProject} from '../../../../../utils/composeExpostProject';
import {mergeArraysAndSortByYear} from '../../../../../utils/mergeArraysAndSortByYear';
import {expostProjectToTable} from '../../../../../utils/expostProjectToTable';

//STYLES
const containerSx = {
  display: 'flex',
  flexDirection: 'column',
  margin: '10px',
  width: '98%',
  pt: 1.25
};

const MonitoringBiomassForm = ({
  selectedInterventionZone,
  exanteProject,
  expostProject,
  onBiomasCO2Close,
  onCreateExpostProject
}) => {

  const {t} = useTranslation();

  const {id, area, centroid, fire_risk, machinery_difficulty} = selectedInterventionZone;

  const isLoading = useMemo(() => !exanteProject.length || !expostProject, [exanteProject, expostProject]);

  const handleBiomasCo2Close = () => onBiomasCO2Close();

  //EXPOST PROJECT
  const expostProjectWithSavedProp = useMemo(() =>
    expostProjectToTable(expostProject), [expostProject]);

  //PLANTING YEAR
  const initialPlantingYear = useMemo(() => {
    if (exanteProject && exanteProject.length) {
      return exanteProject.at(-1).year_data - exanteProject.at(-1).initial_age + 1;
    } else return 0;
  }, [exanteProject]);

  const [plantingYear, setPlantingYear] = useState(initialPlantingYear);

  useEffect(() => {
    setPlantingYear(initialPlantingYear);
  }, [initialPlantingYear]);

  //INVENTORIES
  const [newInventories, setNewInventories] = useState([]);
  const inventories = useMemo(() => expostProjectWithSavedProp?.inventories || [], [expostProjectWithSavedProp]);
  const hasInventories = useMemo(() => !!mergeArraysAndSortByYear(inventories, newInventories).length, [inventories, newInventories]);

  const handleInventoryAdd = (inventory) => setNewInventories([...newInventories, inventory]);
  const handleDeleteInventory = (inventories) => setNewInventories(inventories);

  //INTERVENTIONS
  const [newInterventions, setNewInterventions] = useState([]);
  const co2Interventions = expostProjectToCO2Interventions(expostProject, plantingYear);

  const handleAddIntervention = (intervention) => setNewInterventions([...newInterventions, intervention]);
  const handleDeleteIntervention = (interventions) => setNewInterventions(interventions);
  const handleUpdateInterventions = (interventions) => setNewInterventions(interventions);

  const removeSavedField = (entities) =>
    entities.map((entity) => {
      // eslint-disable-next-line no-unused-vars
      const { saved, ...remainingFields } = entity;
      return { ...remainingFields };
    });

  //EXANTE PROJECT
  const baselinePrediction = useMemo(() =>
    exanteProject?.find(project => project.type === BASELINE_CO2)?.prediction || []
  , [exanteProject]);

  const minYear = useMemo(() => 
    getMinYearCO2Interventions(exanteProject, co2Interventions, newInterventions)
  , [exanteProject, co2Interventions, newInterventions]);
  
  const maxYear = useMemo(() => exanteProject.length
    ? exanteProject.at(-1).initial_year + exanteProject.at(-1).time_frame
    : getCurrentYear(), [exanteProject]);
  
  const initialYear = useMemo(() => exanteProject.length
    ? exanteProject.at(-1).initial_year
    : getCurrentYear(), [exanteProject]);

  //EXPOST PROJECT
  const handleCalculateCO2 = () => {
    onCreateExpostProject(
      composeExpostProject(
        selectedInterventionZone.id,
        mergeArraysAndSortByYear(removeSavedField(inventories), newInventories),
        mergeArraysAndSortByYear(removeSavedField(co2Interventions), newInterventions)
      )
    );
    setNewInventories([]);
    setNewInterventions([]);
  };

  const expostProjectPrediction = useMemo(() => expostProject && expostProject[0]?.prediction, [expostProject]);
  
  const speciesName = useMemo(() =>
    SPECIES_INCLUDED_BIOMASCO2.find(s => s.xorforId === exanteProject[0]?.species_id)?.name || selectedInterventionZone?.species[0].label
  , [exanteProject, selectedInterventionZone]);

  return <>
    {
      isLoading
        ? <LazyLoading isLoading={isLoading}/>
        : <Box sx={containerSx}>
          {/*DESCRIPCIÓN DE LA MASA----------------------------------------------------------------------------------------*/}
          <BiomasSection number={1} label={t('Descripción de la masa')}>
            <>
              <Box sx={{display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: 4}}>
                <MonitoringMassDescription
                  id_rodal={id}
                  initialPlantingYear={initialPlantingYear}
                  area={area}
                  centroid={[centroid.lat, centroid.lon]}
                  species_label={speciesName}
                  fire_risk={fire_risk}
                  machinery_difficulty={machinery_difficulty}
                />
              </Box>
            </>
          </BiomasSection>
          {/*INVENTARIOS---------------------------------------------------------------------------------------------------*/}
          <BiomasSection number={2} label={t('Inventarios')} sx={{display: 'flex', flexDirection: 'column', mb: 0}}>
            <InventoriesMenu
              minYear={minYear}
              maxYear={maxYear}
              inventories={inventories}
              newInventories={newInventories}
              plantingYear={plantingYear}
              onInventoryAdd={(inventory) => handleInventoryAdd(inventory)}
              onInventoryUpdate={() => {}}
              onInventoriesDelete={(inventories) => handleDeleteInventory(inventories)}
            />
          </BiomasSection>

          {/*ACTUACIONES---------------------------------------------------------------------------------------------------*/}
          <BiomasSection number={3} label={t('Actuaciones')} sx={{display: 'flex', flexDirection: 'column'}}>
            <Co2_InterventionMenu
              minYear={minYear}
              maxYear={maxYear}
              hasInventories={hasInventories}
              co2Interventions={co2Interventions}
              newCo2Interventions={newInterventions}
              plantingYear={plantingYear}
              onCo2InterventionAdd={(intervention) => handleAddIntervention(intervention)}
              onCo2InterventionUpdate={(interventions) => handleUpdateInterventions(interventions)}
              onCo2NewInterventionsChange={(interventions) => handleDeleteIntervention(interventions)}
              onCalculateCO2={handleCalculateCO2}
              onPlantingYearChange={setPlantingYear}
            />
          </BiomasSection>

          {/*BALANCE DE CO2------------------------------------------------------------------------------------------------*/}
          <BiomasSection number={4} label={t('Balance de CO2')} sx={{display: 'flex', flexDirection: 'column', mt: 2}}>
            <AbsorptionPlanSeccion
              minChartYear={initialYear}
              maxChartYear={maxYear}
              firstCalculation={{type: BASELINE_CO2, prediction: baselinePrediction}}
              finalCalculation={{type: MONITORING, prediction: expostProjectPrediction}}
              isMonitoring={true}
            />
          </BiomasSection>
          <Button variant='contained' color='secondary' sx={{ml: 4, my: 2, width: '250px'}} onClick={handleBiomasCo2Close}>{t('salir')}</Button>
        </Box>
    }
  </>;

};

export default MonitoringBiomassForm;

MonitoringBiomassForm.propTypes = {
  inventories: PropTypes.arrayOf(inventory),
  isOffline: PropTypes.bool,
  expostProject: PropTypes.arrayOf(expostProject),
  exanteProject: PropTypes.array,
  selectedInterventionZone: PropTypes.object.isRequired,
  onBiomasCO2Close: PropTypes.func.isRequired,
  onCreateExpostProject: PropTypes.func.isRequired
};